import React, {useEffect} from "react";
import {useStores} from "../../../stores";
import type {Bag} from "../../../services/classes/Bag";
import {useLocation, useNavigate} from "react-router";
import {FormControl, Skeleton} from "@mui/material";
import type {SxProps, Theme} from "@mui/material/styles";
import {observer} from "mobx-react-lite";
import {SelectDatasetMenu} from "../../_tiny/SelectDatasetMenu";
import Stack from "@mui/material/Stack";
import {environment} from "../../../env.ts";
import {DatabagAdminEditModal} from "~/components/main/header/DatabagAdminEditModal.tsx";
import DbIcon from "~/icons/svg/DbIcon.svg?react";
import {COLORS} from "~/style/colors-ts.ts";

/** Mithra 3.1 data bag selector: outlined 36px dropdown with a database glyph */
const SELECT_SX: SxProps<Theme> = {
    height: 36,
    minWidth: 160,
    maxWidth: 200,
    borderRadius: '8px',
    backgroundColor: COLORS.white[900],
    color: COLORS.text.primary,
    fontSize: 13,
    fontWeight: 500,
    // Block (not flex) so a long data bag name ends in an ellipsis
    '& .MuiSelect-select': {pl: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
    '& .MuiSelect-select svg': {verticalAlign: 'middle', mr: 0.5},
    '& .MuiOutlinedInput-notchedOutline': {borderColor: COLORS.grey[100]},
    '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.grey[400],
        borderWidth: 1,
    },
};

/** Between the selector's min and max width, as a typical data bag name takes. */
const SKELETON_WIDTH = 180;

const MENU_PROPS = {
    anchorOrigin: {vertical: 'bottom', horizontal: 'right'},
    transformOrigin: {vertical: 'top', horizontal: 'right'},
    slotProps: {paper: {sx: {minWidth: 240, mt: 0.75}}},
} as const;

const dbIcon = <DbIcon width={16} height={16} style={{flexShrink: 0}}/>;

type Props = {
    disabled?: boolean;
    showEmpty?: boolean;
}
export const SelectDataset: React.FC<Props> = observer(({disabled, showEmpty}) => {
    const {bagStore, authStore} = useStores();
    const bag = bagStore.bag as Bag;
    const value = bag?.id || '';
    const location = useLocation();
    const navigate = useNavigate();
    const isStaff = environment.isTest || authStore.isMithraStaffGroup;
    const bags = bagStore.userBagChoices;
    useEffect(() => {
        if (!bags) {
            // TODO: The select-dataset is reloaded when the page is changed, this can be optimized
            //  This component is unloaded when the router changes, pull the component higher up in the route chain
            bagStore.getAllBags()
        }
    }, [bagStore, bags])

    if (bags && !bag) {
        return null;
    }

    return <FormControl size="small">
        {!bags
            // The data bags are loading: a skeleton the selector's size, not an empty disabled one.
            ? <Skeleton variant="rounded" width={SKELETON_WIDTH} height={36} sx={{borderRadius: '8px'}}/>
            : <Stack direction="row" spacing={1} alignItems="center">
                <SelectDatasetMenu
                    value={value}
                    startAdornment={dbIcon}
                    MenuProps={MENU_PROPS}
                    sx={SELECT_SX}
                    showSelectedCheck
                    onChange={event => {
                        const bagId = event.target.value;
                        bagStore.changeBag(bagId, navigate, location);
                    }}
                    // displayEmpty
                    disabled={disabled}
                    bags={bags}
                />
                {
                    isStaff && <DatabagAdminEditModal bag={bag}/>
                }
            </Stack>}
    </FormControl>
})
